import { createSelector } from '@ngrx/store';

import { FinnairServiceCatalogItemV3 } from '@fcom/dapi/api/models/finnair-service-catalog-item-v-3';
import {
  ServiceCatalog,
  ServiceCatalogCategory,
  ServiceCatalogCategoryServicesMap,
} from '@fcom/dapi/interfaces/services.interface';
import { isPresent, mapValues } from '@fcom/core/utils/utils';
import { isTopTierMemberSelector } from '@fcom/core/selectors/login.selector';

import { isSeatBundle } from '../../modules/ancillaries/utils/category.utils';
import { asServiceCatalogCategory } from '../../modules/ancillaries/utils/service-catalog.utils';
import { CommonBookingState } from '../store.interface';
import { selectCommonBookingState } from './feature.selector';

const servicesStateSelector = createSelector(selectCommonBookingState, (state: CommonBookingState) => state?.services);

const servicesCatalogSelector = createSelector(servicesStateSelector, (servicesState) => servicesState?.services);

/**
 * Returns all available seat bundles for all the flights in the seatmap,
 * filtered to only include categories that are eligible to sell according to cart/order eligibilities.
 */
export const seatBundleCatalogByFlightSelector = createSelector(
  selectCommonBookingState,
  (state: CommonBookingState) => {
    const eligibilities = state?.services?.bundleEligibilities;
    return mapValues(state?.seatMap?.seatmaps || {}, (flight, flightId) => {
      const items = flight?.seatMap?.bundleServiceCatalogItems || [];
      if (!eligibilities?.categories?.length) {
        return items;
      }
      return items.filter((item) => {
        const categoryEligibilities = eligibilities.categories.filter((eligCat) => eligCat.category === item.category);
        if (!categoryEligibilities.length) {
          return true;
        }
        return categoryEligibilities.some((eligCat) =>
          Object.values(eligCat.services[flightId] || {}).some((paxElig) => paxElig.isAllowedToUse)
        );
      });
    });
  }
);

// Merge the seatmap-derived seat bundles across flights (the same bundle appears once per flight).
const seatmapSeatBundlesSelector = createSelector(
  seatBundleCatalogByFlightSelector,
  (
    bundleCatalogByFlight: ReturnType<typeof seatBundleCatalogByFlightSelector.projector>
  ): FinnairServiceCatalogItemV3[] =>
    Object.entries(bundleCatalogByFlight ?? {}).reduce((seatmapBundles, [_flightId, items]) => {
      items.forEach((bundle) => {
        const matchingBundleIndex = seatmapBundles.findIndex(
          (seatmapBundle: FinnairServiceCatalogItemV3) => seatmapBundle.category === bundle.category
        );

        if (matchingBundleIndex !== -1) {
          seatmapBundles[matchingBundleIndex] = {
            ...seatmapBundles[matchingBundleIndex],
            services: { ...bundle?.services, ...seatmapBundles[matchingBundleIndex].services },
            lowestPrice: { ...bundle?.lowestPrice, ...seatmapBundles[matchingBundleIndex].lowestPrice },
          };
        } else {
          seatmapBundles.push(bundle);
        }
      });

      return seatmapBundles;
    }, [] as FinnairServiceCatalogItemV3[])
);

// Seat bundles sourced from the seatmap (the default source of truth) and prepended to the catalog categories.
export const serviceCatalogSelector = createSelector(
  servicesCatalogSelector,
  seatmapSeatBundlesSelector,
  isTopTierMemberSelector,
  (services: ServiceCatalog, seatmapBundleItems: FinnairServiceCatalogItemV3[], isTopTierMember: boolean) => {
    // Do not fall back to service catalogue response for top tier members due
    // to discrepancies between the seatmap and the service catalogue for accompanying passengers.
    if (!isPresent(services) || (!isTopTierMember && !seatmapBundleItems.length)) {
      return services;
    }

    const seatmapBundleCategories = new Set(seatmapBundleItems.map((bundle) => bundle.category));
    const catalogRest = (services.categories || []).filter((category) =>
      isTopTierMember ? !isSeatBundle(category.category) : !seatmapBundleCategories.has(category.category)
    );

    return {
      ...services,
      categories: [...seatmapBundleItems.map((bundle) => asServiceCatalogCategory(bundle)), ...catalogRest],
    };
  }
);

// Prefer the cart catalog seat bundle where present, falling back to the seatmap-derived one, so the same
// bundle is never shown twice. Ordering: cart catalog bundles, then seatmap fallbacks, then other categories.
export const serviceCatalogFromCatalogSeatBundlesSelector = createSelector(
  servicesCatalogSelector,
  seatmapSeatBundlesSelector,
  (services: ServiceCatalog, seatmapBundleItems: FinnairServiceCatalogItemV3[]) => {
    if (!isPresent(services) || !seatmapBundleItems.length) {
      return services;
    }

    const catalogCategories = services.categories || [];
    const catalogBundleCategories = new Set(
      catalogCategories.filter((category) => isSeatBundle(category.category)).map((category) => category.category)
    );
    const seatmapBundles: ServiceCatalogCategory[] = seatmapBundleItems
      .filter((bundle) => !catalogBundleCategories.has(bundle.category))
      .map((bundle) => asServiceCatalogCategory(bundle));
    const catalogBundles = catalogCategories.filter((category) => isSeatBundle(category.category));
    const catalogRest = catalogCategories.filter((category) => !isSeatBundle(category.category));

    return {
      ...services,
      categories: [...catalogBundles, ...seatmapBundles, ...catalogRest],
    };
  }
);

export const serviceCategoriesSelector = createSelector(serviceCatalogSelector, (catalog) => catalog?.categories);

export const serviceCategoriesFromCatalogSeatBundlesSelector = createSelector(
  serviceCatalogFromCatalogSeatBundlesSelector,
  (catalog) => catalog?.categories
);

export const serviceCategoriesMapSelector = createSelector(
  serviceCatalogSelector,
  (catalog): ServiceCatalogCategoryServicesMap | undefined =>
    catalog?.categories.reduce<ServiceCatalogCategoryServicesMap>((categories, category) => {
      categories[category.category] = category.services;
      return categories;
    }, {})
);
